<script lang="ts" setup>
    import IdMeMfa from '../IdMeVerify/IdMeMfa.vue'
    import _ from 'lodash';
    import { computed, reactive, onMounted, nextTick } from 'vue';
    import { ChoiceApi, LoadingSection } from '../../LibMain';
    import { PrescriptionTypeValues, PrescriptionAndTypeModel } from '../../lib/Api/Prescription';
    import { popAlert } from '../../lib/Notification';
    import { DisplayStyle, TextDisplayStyle } from '../../Enums/UiEnums';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { ICol } from '../../lib/ChoiceTableHelpers';
    import MessageText from "../../components/Messages/MessageText.vue"
    import MessageSection from "../../components/Messages/MessageSection.vue"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ChoiceTable from '../../components/ChoiceTable.vue';
    import { closeModal } from '../../lib/Legacy/ModalWindow';
    import { XmlDocument } from '../../lib/XmlDocument'
    import MessageBadge from '../../components/Messages/MessageBadge.vue';

    interface EpcsPrescriptionInfoModel
    {
        prescriptionId: number,
        prescriptionType?: PrescriptionTypeValues,
        certSerialNumber?: string,
        prescriptionDrug?: string,
        prescriptionDirections?: string,
        prescriptionNote?: string,
        prescriberDEA?: string,
        prescriberName?: string,
        patientName?: string,
        pharmacyName?: string,
        rxMessage?: string,
        refilResponseCode?: string,
        rxRefNumber?: string,
        companyName?: string,
        serviceLevelDesc?: string,
        //xml?: string,
        epcsChangeDataXml?: string,
        status?:{
            transmissionErrMsg?: string
        }
    }

    enum StepTypes
    {
        showPrescriptionInfo,
        showMfa,
        sendPrescriptions
    }

    const props = defineProps<{
        epcsXmlArray: string[],
        epcsChangeDataXmlArray?: string[],
        queueSigning?: boolean,
        suppressDeclineRxApiCall?: boolean      // Clean up the prescription unless we're told not to (legacy code may clean up for us)
    }>();

    const compData = reactive<{
        epcsInfo: (EpcsPrescriptionInfoModel)[],
        step?: StepTypes
    }>({
        epcsInfo: [],
        step: StepTypes.showPrescriptionInfo
    });


    const errorMessages = computed(()=>{
        const errMsgs = [];
        if (0 === props.epcsXmlArray.length)
            errMsgs.push("No EPSC data provided");

        // Make sure prescriber and patient names are unique
        if (prescriberNameArray.value.length > 1)
            errMsgs.push(`More than one prescriber name: ${prescriberNameArray.value.join(", ")}`);
        if (patientNameArray.value.length > 1)
            errMsgs.push(`More than one patient name: ${patientNameArray.value.join(", ")}`);

        return errMsgs;
    });
  
    const emit = defineEmits<{
        (e: "closeSigning", signing_status: string): void
    }>();
    onMounted(async () =>{
        await nextTick();

        // Convert EPCS XML data to JSON
        for(let index=0; index < props.epcsXmlArray.length; ++index)
        {
            const epcsItemXml = props.epcsXmlArray[index];
            const epcsChangeDataXml = props.epcsChangeDataXmlArray ? props.epcsChangeDataXmlArray[index] : undefined;

            compData.epcsInfo.push(getEpcsObject(epcsItemXml, epcsChangeDataXml));
        }
    });

    function getEpcsObject(epcsXml: string, epcsChangeDataXml: string|undefined): EpcsPrescriptionInfoModel
    {
        // Convert the EPCS XML data to JSON
        const xmlEpsc = XmlDocument.stringToXmlDocument(epcsXml);

        return {
            prescriptionId: xmlEpsc.getNumber("EPCS > PrescriptionID") ?? 0,
            prescriptionType: xmlEpsc.getValueString_NonWhitespace("EPCS > PrescriptionType") as PrescriptionTypeValues,
            certSerialNumber: xmlEpsc.getValueString("EPCS > CertSerialNumber"),
            prescriptionDrug: xmlEpsc.getValueString("EPCS > PrescriptionDrug"),
            prescriptionDirections: xmlEpsc.getValueString("EPCS > PrescriptionDirections"),
            prescriptionNote: xmlEpsc.getValueString("EPCS > PrescriptionNote"),
            prescriberDEA: xmlEpsc.getValueString("EPCS > PrescriberDEA"),
            prescriberName: xmlEpsc.getValueString("EPCS > PrescriberName"),
            patientName: xmlEpsc.getValueString("EPCS > PatientName"),
            pharmacyName: xmlEpsc.getValueString("EPCS > PharmacyName"),
            rxMessage: xmlEpsc.getValueString("EPCS > RxMessage"),
            refilResponseCode: xmlEpsc.getValueString("EPCS > RefilResponseCode"),
            rxRefNumber: xmlEpsc.getValueString("EPCS > RxRefNumber"),
            companyName: xmlEpsc.getValueString("EPCS > CompanyName"),
            serviceLevelDesc: xmlEpsc.getValueString("EPCS > ServiceLevelDesc"),
            epcsChangeDataXml: epcsChangeDataXml
        };
    }

    const signatureMessage = "By completing the two-factor authentication at this time, you are authorizing the registration of this credential/certificate for digitally signing. The two-factor authentication protocol may only be completed by the owner of this credential."

    async function onMfaSuccess()
    {        
        // Get the prescriptions we've not successfully sent yet
        const prescriptionsToSend = compData.epcsInfo
                                        .filter(item => undefined === item.status || item.status.transmissionErrMsg)
                                        .map(rx => ({
                                                prescriptionId: rx.prescriptionId, 
                                                prescriptionType: rx.prescriptionType, 
                                                rxRefNumber: rx.rxRefNumber,
                                                refilResponseCode: rx.refilResponseCode,
                                                serviceLevelDesc: rx.serviceLevelDesc,
                                                epcsChangeDataXml: rx.epcsChangeDataXml,
                                                messageForSignature: rx.rxMessage
                                            }));

        // Make sure we have something to send                                            
        if (0 === (prescriptionsToSend.length ?? 0))
        {
            alert("There are no prescriptions to send");
            return;
        }

        compData.step = StepTypes.sendPrescriptions;

        // Save approval of prescriptions
        const resp = await ChoiceApi.prescription.RxApprove({
                prescriptions: prescriptionsToSend,
                signatureMode: "MessageSignatureModes.IdMe"
            });

        if (!resp)
        {
            await popAlert("No data returned");
            compData.step = StepTypes.showPrescriptionInfo;
            return;
        }

        if (!resp.find(item => item.errorMsg))
        {
            const successMsg = (1 === compData.epcsInfo.length) ? "Prescription has been sent" : "Prescriptions have been sent";
            await popAlert(successMsg);

            // Close the dialog
            if (props.queueSigning ?? false) {
                emit("closeSigning","Approved"); 
                return;
            }
            closeModal(true, undefined, { legacyReturnValue: "Approved" });
            return;
        }

        // Update the prescription info
        for(const rx of resp)
        {
            const prescription = compData.epcsInfo.find(item => item.prescriptionId == rx.prescriptionId);
            if (!prescription)
            {
                alert("Unable to find matching prescription");
                continue;
            }

            if (rx.errorMsg)
            {
                prescription.status = { transmissionErrMsg: rx.errorMsg};
            }
            else
            {
                prescription.status = { transmissionErrMsg: undefined };
            }
        }
        compData.step = StepTypes.showPrescriptionInfo;
    }

    async function onMfaFailure(errorMsg?: string)
    {
        if (errorMsg)
            await popAlert(`MFA failure: ${errorMsg}`);

        compData.step = StepTypes.showPrescriptionInfo;
    }

    const patientNameArray = computed<(string|undefined)[]>(() => {
        return _.uniq(compData.epcsInfo.map(item => item.patientName))
                    .filter(name => undefined !== name);
    });

    const patientName = computed<string|undefined>(() => 
        (1 == patientNameArray.value.length) ? _.first(patientNameArray.value) : undefined);    

    const prescriberNameArray = computed<(string|undefined)[]>(() => {
        return _.uniq(compData.epcsInfo.map(item => item.prescriberName))
                    .filter(name => undefined !== name);
    });

    const prescriberName = computed<string|undefined>(() => 
        (1 == prescriberNameArray.value.length) ? _.first(prescriberNameArray.value) : undefined);


    // Show the MFA options
    async function onClickAgreeButton()
    {
        compData.step = StepTypes.showMfa;
    }

    async function onClickDeclineButton()
    {
        // Cancel the prescriptions
        await declinePrescriptions();
        if (props.queueSigning ?? false) {
            emit("closeSigning","Declined"); 
            return;
        }
        closeModal(true);
        await popAlert("Prescription declined");
        window.close();
    }    

    const invalidEpscData = computed(() => 1 !== prescriberNameArray.value.length || 1 !== patientNameArray.value.length );

    const prescriptionTableColumns = computed<ICol[]>(() => {
        const columns = [
            {Name: "Pharmacy", DataField: "pharmacyName"},
            {Name: "Medication", DataField: "prescriptionDrug" },
            {Name: "Id", DataField: "prescriptionId" }
        ];

        if (compData.epcsInfo.find(rx => rx.status))
        {
            columns.push({Name: "Status", DataField: "status" });
        }
        return columns;
    });

    async function declinePrescriptions()
    {
        if (!props.suppressDeclineRxApiCall)
        {
            // Cancel the prescriptions
            await ChoiceApi.prescription.RxDecline(prescriptionAndTypeItems.value);
        }
    }

    const prescriptionAndTypeItems = computed<PrescriptionAndTypeModel[]>(() => 
        compData.epcsInfo.map(rx => { 
            return { prescriptionId: rx.prescriptionId, prescriptionType: rx.prescriptionType }
        }));
</script>

<template>
    <MessageSection v-if="errorMessages.length" :displayStyle="DisplayStyle.Danger">
        <div v-for="errMsg in errorMessages">
            {{ errMsg }}
        </div>
    </MessageSection>

    <template v-if="!errorMessages.length">
        <!-- Confirm the prescription before showing the MFA info-->
        <div v-if="StepTypes.showPrescriptionInfo === compData.step">
            <div>
                <b>Prescriber: </b> {{ prescriberName }}
            </div>
            <div>
                <b>Patient: </b> {{ patientName }}
            </div>
            <div>
                <ChoiceTable :columns="prescriptionTableColumns" :data="compData.epcsInfo">
                    <template #cell="{col, row, value}">
                        <template v-if="'Status' === col.Name">
                            <MessageSection v-if="value.transmissionErrMsg" :displayStyle="DisplayStyle.Danger">
                                <MessageBadge :displayStyle="DisplayStyle.Danger">Problem</MessageBadge>
                                {{ value.transmissionErrMsg }}
                            </MessageSection>
                            <MessageBadge v-if="!value.transmissionErrMsg" :displayStyle="DisplayStyle.Success">Sent!</MessageBadge>
                        </template>
                    </template>
                </ChoiceTable>
            </div>
    
            <hr/>
            <MessageText :displayStyle="TextDisplayStyle.Muted">
                {{ signatureMessage }}
            </MessageText>
            
            <div class="mt-2">
                <span class="me-2">
                    <ActionButtonV2 :action="ActionButtonStyle.Save" :performAction="onClickAgreeButton" text="I Agree" />
                </span>
                <ActionButtonV2 :action="ActionButtonStyle.Save" :performAction="onClickDeclineButton" text="I Decline" :displayStyle="DisplayStyle.Danger" />
            </div>
    
        </div>
        
        <div v-if="StepTypes.showMfa === compData.step" >
            <div>
                <strong>
                    Multi-Factor Authentication Options
                </strong>
            </div>
            <IdMeMfa @mfaSuccess="onMfaSuccess" @mfaFailure="onMfaFailure" />
        </div>

        <div v-if="StepTypes.sendPrescriptions === compData.step">
            <LoadingSection loadingMsg="Sending prescription data..." />
        </div>
    </template>

</template>